import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Star,
  MapPin,
  Phone,
  ArrowRight,
  CheckCircle,
  Award,
  Building2,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const industryNames = {
  hvac: "HVAC & Heating",
  plumbing: "Plumbing",
  electrical: "Electrical",
  home_repair: "Home Repair",
  roofing: "Roofing",
  landscaping: "Landscaping",
  auto_repair: "Auto Repair",
  septic: "Septic Services",
  dental: "Dental & Medical",
  restaurant: "Restaurant",
  fishing_charter: "Fishing Charter",
  sightseeing: "Sightseeing Tours",
  gym: "Gym & Fitness"
};

const StarRating = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`w-4 h-4 ${
          star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-600'
        }`}
      />
    ))}
  </div>
);

export default function FeaturedContractors() {
  const navigate = useNavigate();
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeatured();
  }, []);

  const fetchFeatured = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/contractors/featured?limit=6`);
      setContractors(response.data.contractors || []);
    } catch (error) {
      console.error('Error fetching featured contractors:', error);
    } finally {
      setLoading(false);
    }
  };

  // Don't render section if no contractors
  if (!loading && contractors.length === 0) {
    return null;
  }

  return (
    <section className="py-20 bg-slate-900/30" data-testid="featured-contractors-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 mb-6">
            <Award className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-purple-400 font-medium">Featured Partners</span>
          </div>
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold mb-4">
            Top-Rated Contractors
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Verified service providers delivering exceptional results across the nation.
          </p>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {contractors.map((contractor, index) => (
              <motion.div
                key={contractor.contractor_id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-900 border border-slate-800 rounded-xl p-6 hover:border-slate-700 transition-all hover:shadow-lg hover:shadow-sky-500/5"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {contractor.logo_url ? (
                      <img 
                        src={contractor.logo_url} 
                        alt={contractor.business_name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-sky-400/20 to-blue-600/20 flex items-center justify-center text-lg font-bold text-sky-400">
                        {contractor.business_name?.charAt(0) || '?'}
                      </div>
                    )}
                    <div>
                      <h3 className="font-bold">{contractor.business_name}</h3>
                      <p className="text-sm text-slate-400">
                        {industryNames[contractor.industry] || contractor.industry}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {contractor.is_featured && (
                      <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full flex items-center gap-1">
                        <Award className="w-3 h-3" />
                        Featured
                      </span>
                    )}
                    {contractor.is_verified && (
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Verified
                      </span>
                    )}
                  </div>
                </div>

                {/* Rating */}
                {contractor.rating && (
                  <div className="flex items-center gap-2 mb-3">
                    <StarRating rating={Math.round(contractor.rating)} />
                    <span className="text-sm font-medium text-yellow-400">{contractor.rating}</span>
                    <span className="text-xs text-slate-500">({contractor.review_count} reviews)</span>
                  </div>
                )}

                {/* Location */}
                <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
                  <MapPin className="w-4 h-4" />
                  {contractor.city}, {contractor.state}
                </div>

                {/* Description */}
                {contractor.description && (
                  <p className="text-sm text-slate-500 line-clamp-2 mb-4">
                    {contractor.description}
                  </p>
                )}

                {/* Services */}
                {contractor.services?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {contractor.services.slice(0, 3).map((service) => (
                      <span
                        key={`${contractor.id}-${service}`}
                        className="px-2 py-1 bg-slate-800 rounded text-xs text-slate-400"
                      >
                        {service}
                      </span>
                    ))}
                  </div>
                )}

                <Button
                  onClick={() => navigate(`/contractors/${contractor.state}`)}
                  variant="outline"
                  className="w-full border-sky-500/50 text-sky-400 hover:bg-sky-500/10"
                  size="sm"
                >
                  View Profile
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
            ))}
          </div>
        )}

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Button
            onClick={() => navigate('/us-demo')}
            size="lg"
            className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
          >
            <Building2 className="w-5 h-5 mr-2" />
            Find a Contractor or Real Estate Pro in Your State
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
